// InfoStep component
import React, { useState, useEffect, useMemo } from 'react';
import Button from '@/components/Button';
import toast from 'react-hot-toast';
import CustomToast from '@/components/CustomToast';
import { AngleLeftIcon } from '@/utils/svgIcons';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useLoginModal } from '@/contexts/LoginModalContext';

export default function InfoStep({
  inputValues,
  inputs,
  is_edit,
  UpdateValue,
  setChildName,
  setInputAge,
  step,
  updateProductData,
  handleNextStep,
  setPersonalizeModal,
  childName,
  inputAge,
  childgender,
  savedData,
  setLoadData,
  loadDataLoading,
  user,
  preferences,
  showSavedData,
  setShowSavedData,
  isPostLoginFlow,
  handleAddNew,
  disableSavedData
}: any) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const {t} = useTranslation('personalizeModal');
  const {locale} = useRouter();
  const { setIsOpen, setProductType, setPostLoginCallback } = useLoginModal();
  
  // Add a ref to track if user has chosen to add new
  const userHasChosenAddNew = React.useRef(false);
  const hasLoadedData = React.useRef(false);

  // Update the useEffect to use the prop setter
  useEffect(() => {
    if (hasLoadedData.current) {
      return;
    }
    // Only auto-show saved data if:
    // 1. We're on the info step
    // 2. User exists
    // 3. User has saved data
    // 4. User hasn't manually chosen "Add new"
    if (step === 'info' && user && savedData.length > 0 && !userHasChosenAddNew.current && !disableSavedData) {
      setShowSavedData(true);
    } else {
      setShowSavedData(false);
    }
  }, [user, savedData, step, setShowSavedData, disableSavedData]);

  // Reset the ref when component unmounts or step changes
  useEffect(() => {
    return () => {
      userHasChosenAddNew.current = false;
    };
  }, []);

  // Function to truncate dedication text
  const truncateDedication = (text: string, maxLength: number = 50) => {
    if (!text) return locale === 'ar' ? 'لا يوجد إهداء' : 'No dedication added';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Function to get cover name
  const getCoverName = (cover: any) => {
    const noCoverSelected = locale === 'ar' ? 'لم يتم الاختيار' : 'No Cover selected';
    const softCover = locale === 'ar' ? 'غلاف ليّن' : 'Soft Cover';
    const hardCover = locale === 'ar' ? 'غلاف مُقَوَّى' : 'Hard Cover';
    if (!cover) return noCoverSelected;
    if (typeof cover === 'object') return cover.name;
    return cover === '1' ? softCover : hardCover;
  };

  // Function to get packaging text
  const getPackagingText = (packaging: any) => {
    const noPackaging = locale === 'ar' ? 'بدون تغليف' : 'No Packaging Added';
    const packagingAdded = locale === 'ar' ? 'تمت إضافة التغليف' : 'Packaging added'; 
    return packaging ? packagingAdded : noPackaging;
  };
  
  // Determine if mobile based on screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Reverse the savedData array to show most recent first
  const reversedSavedData = useMemo(() => {
    // First filter the data based on gender match
    const filteredData = savedData.filter((data: any) => {
      // If childgender is not provided or data doesn't have gender, include all
      if (!childgender || !data.gender) return true;
      // Check if genders match (case insensitive comparison)
      return data.gender.toLowerCase() === childgender.toLowerCase();
    });
    
    // Then reverse the filtered array to show most recent first
    return [...filteredData].reverse();
  }, [savedData, childgender]);

  
  // Calculate total slides - always include "Add new" option
  const itemsPerSlide: number = isMobile ? 1 : 3;
  // We subtract 1 from itemsPerSlide because we always reserve a spot for "Add new" on the first slide
  const itemsPerSlideWithoutAddNew = itemsPerSlide - 1;
  
  // For desktop: we need to show saved data + "Add new" card
  // The first slide will have (itemsPerSlide - 1) saved data items + "Add new" card
  // Subsequent slides will have full itemsPerSlide saved data items
  const totalSlides = Math.ceil(Math.max(0, reversedSavedData.length - itemsPerSlideWithoutAddNew) / itemsPerSlide) + 1;
  
  // Get current slide items
  const getCurrentSlideItems = () => {
    const slideItems = [];
    
    if (currentSlide === 0) {
      // First slide: show "Add new" + up to (itemsPerSlide - 1) saved items
      slideItems.push(null); // "Add new" card always first on first slide
      
      // Add saved data items for first slide
      const firstSlideSavedItems = reversedSavedData.slice(0, itemsPerSlideWithoutAddNew);
      slideItems.push(...firstSlideSavedItems);
    } else {
      // Subsequent slides: only saved data items
      const startIndex = itemsPerSlideWithoutAddNew + (currentSlide - 1) * itemsPerSlide;
      const endIndex = Math.min(startIndex + itemsPerSlide, reversedSavedData.length);
      
      for (let i = startIndex; i < endIndex; i++) {
        if (i < reversedSavedData.length) {
          slideItems.push(reversedSavedData[i]);
        }
      }
    }
    
    return slideItems;
  };
  
  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };
  
  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };
  // Handle data selection
  const handleDataSelection = (data: any) => {
    // if (!user) {
    //   // If user is not logged in, redirect to login
    //   toast.custom((tel) => <CustomToast type='error' t={tel} message={t('login_first')} />, {
    //     duration: 2500,
    //   });
    //   setProductType('1');
    //   setPostLoginCallback(() => {
    //     // After login, show saved data again
    //     userHasChosenAddNew.current = false;
    //     hasLoadedData.current = false;
    //     setShowSavedData(true);
    //   });
    //   setIsOpen(true);
    //   return;
    // }
    
    if (data) {
      // User selected existing data
      userHasChosenAddNew.current = false;
      hasLoadedData.current = true;
      setLoadData(data);
      setShowSavedData(false);
      // DON'T call handleNextStep here - let the useEffect in PersonalizeModal handle it
      // after the data is fully loaded
    } else {
      // User clicked "Add new"
      if (handleAddNew) {
        handleAddNew(); // Call the parent's handleAddNew
      }
      userHasChosenAddNew.current = true;
      hasLoadedData.current = false;
      setShowSavedData(false);
      // Also reset current slide for next time
      setCurrentSlide(0);
      
      // Clear any existing loaded data since we're starting fresh
      setLoadData(null);
      // Don't call UpdateValue here - let the parent component handle it
    }
  };

  useEffect(() => {
    return () => {
      userHasChosenAddNew.current = false;
      hasLoadedData.current = false;
    };
  }, []);

  // Reset form fields when showing saved data
  // useEffect(() => {
  //   if (showSavedData) {
  //     // Reset form fields when showing saved data selection
  //     const vals: any = {};
  //     Object.keys(inputs).forEach((input: string) => {
  //       vals[input] = '';
  //     });
  //     // Reset child name and age when showing saved data
  //     if (setChildName) setChildName('');
  //     if (setInputAge) setInputAge('');
  //   }
  // }, [showSavedData, inputs, setChildName, setInputAge]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 bg-[#FFFCEB] h-full" id='info-step'>
      {/* Saved Data Selection */}
      {step === 'info' && showSavedData && user && !isPostLoginFlow && (
        <div className="lg:mx-auto lg:max-w-4xl mb-6">
          <h2 className="text-xl font-bold mb-6 text-center">{t('importDataHeading')}</h2>
          
          {/* Mobile View - Horizontal Scroll */}
          {isMobile ? (
            <div className="overflow-y-auto pb-4 md:hidden max-h-[80vh]">
              <div className="flex flex-col space-y-4" 
                dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
              >
                {reversedSavedData.map((data, index) => (
                  <div 
                    key={index}
                    className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-primary-500 transition-colors bg-white min-h-[300px] w-full flex flex-col justify-center"
                    onClick={() => handleDataSelection(data)}
                  >
                    <div className="w-32 h-32 mx-auto mb-3 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-primary-100">
                      {data.croppedImageBase64 ? (
                        <img
                          src={data.croppedImageBase64}
                          alt={data.childName || 'Child'}
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          {t('noImage')}
                        </div>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-lg mb-2 text-center">{data.childName ? data.childName : locale === 'ar' ? 'بدون اسم' : 'No name'}</h3>
                    <p className={`text-sm text-gray-600 mb-2 ${locale === 'ar' ? 'text-right': 'text-left'}`}>
                      {t('dedication')} {truncateDedication(data.inputDedication)}
                    </p>
                    <p className={`text-sm text-gray-600 mb-2 ${locale === 'ar' ? 'text-right': 'text-left'}`}>
                      {t('cover')} {getCoverName(data.inputCover)}
                    </p>
                    {preferences?.countryCode === 'IL' && (
                      <p className={`text-sm text-gray-600 ${locale === 'ar' ? 'text-right': 'text-left'}`}>
                        {t('packaging')} {getPackagingText(data.inputPackaging)}
                      </p>
                    )}
                  </div>
                ))}
                
                {/* Add New Card */}
                <div 
                  className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-primary-500 transition-colors bg-white min-h-[300px] w-full flex flex-col justify-center"
                  onClick={() => handleDataSelection(null)}
                >
                  <div className="flex flex-col items-center justify-center h-full">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 border-2 border-primary-100">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <p className="text-center text-gray-600">
                      {t('newData')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Desktop View - Carousel */
          <div className="hidden md:block">
            {/* Carousel Container */}
            <div className="relative">
              {/* Carousel Items */}
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                >
                  {Array.from({ length: totalSlides }).map((_, slideIndex) => {
                    const slideItems = getCurrentSlideItemsForSlide(slideIndex);
                    
                    return (
                      <div key={slideIndex} className="w-full flex-shrink-0 px-2">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {slideItems.map((data, index) => (
                            data === undefined ? (
                              // Empty placeholder to maintain grid structure
                              <div key={index} className="border-2 border-transparent rounded-lg p-4" />
                            ) : data === null ? (
                              // "Add new" card
                              <div
                                key={index}
                                className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-primary-500 transition-colors bg-white min-h-[300px] flex flex-col justify-center"
                                onClick={() => handleDataSelection(null)}
                              >
                                <div className="flex flex-col items-center justify-center h-full">
                                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 border-2 border-primary-100">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                  </div>
                                  <p className="text-center text-gray-600">{t('newData')}</p>
                                </div>
                              </div>
                            ) : (
                              // Saved data card
                              <div
                                key={index}
                                className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-primary-500 transition-colors bg-white min-h-[300px] flex flex-col justify-center"
                                onClick={() => handleDataSelection(data)}
                              >
                                <div className="w-32 h-32 mx-auto mb-3 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-primary-100">
                                  {data.croppedImageBase64 ? (
                                    <img
                                      src={data.croppedImageBase64}
                                      alt={data.childName || 'Child'}
                                      className="object-cover w-full h-full"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                      {t('noImage')}
                                    </div>
                                  )}
                                </div>
                                
                                <h3 className="font-semibold text-lg mb-2 text-center">{data.childName || `${t('withoutName')}`}</h3>
                                <p className={`text-sm text-gray-600 mb-2 ${locale === 'ar' ? 'text-right':'text-left'}`}>
                                  {t('dedication')} {truncateDedication(data.inputDedication)}
                                </p>
                                <p className={`text-sm text-gray-600 mb-2 ${locale === 'ar' ? 'text-right':'text-left'}`}>
                                  {t('cover')} {getCoverName(data.inputCover)}
                                </p>
                                <p className={`text-sm text-gray-600 ${locale === 'ar' ? 'text-right':'text-left'}`}>
                                  {t('packaging')} {getPackagingText(data.inputPackaging)}
                                </p>
                              </div>
                            )
                          ))}
                          
                          {/* Fill remaining empty slots with placeholders */}
                          {slideItems.length < itemsPerSlide && Array.from({ length: itemsPerSlide - slideItems.length }).map((_, index) => (
                            <div key={`placeholder-${index}`} className="border-2 border-transparent rounded-lg p-4" />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Navigation Arrows - Only show if there are multiple slides */}
              {totalSlides > 1 && (
                <>
                  <button 
                    onClick={prevSlide}
                    disabled={currentSlide === 0}
                    aria-label="Previous"
                    className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md disabled:opacity-50 border-2 border-gray-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button 
                    onClick={nextSlide}
                    disabled={currentSlide === totalSlides - 1}
                    aria-label="Next"
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md disabled:opacity-50 border-2 border-gray-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
              
              {/* Dots Indicator - Always show if more than 1 item */}
              {totalSlides > 1 && (
                <div className="flex justify-center mt-8">
                  {Array.from({ length: totalSlides }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      aria-label='dots-indicator'
                      className={`w-3 h-3 rounded-full mx-1 ${
                        index === currentSlide ? 'bg-primary-500' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {/* Regular Input Form - Show when NOT showing saved data OR when step is edit */}
      {(!showSavedData || step !== 'info') && (
        <div className='lg:mx-auto lg:max-w-xl' dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
          <h1 className="text-2xl font-normal text-black text-center">{t('infoStepHeading')}</h1>
          <div className="inputs flex flex-col gap-4 items-center mt-6">
            {Object.entries(inputValues).map(([key, value]) => {
              const isEmpty = value === null || value === undefined || 
              (typeof value === 'string' && value.trim() === '');

              if (isEmpty && is_edit === '1') {
                return null;
              }

              return (
                <div key={key} className="w-full form-group" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                  <label className={`block text-xs ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
                    {key === 'name' && `${t('infoStepEnterName')}`}
                    {key === 'first_name' && `${t('infoStepEnterFirstName')}`}
                    {key === 'age' && `${t('infoStepEnterAge')}`}
                    {key === 'init' && `${t('infoStepEnterInitial')}`}
                  </label>
                  <input
                    type={key === 'age' ? 'number' : 'text'}
                    name={key}
                    min="1"
                    max="100"
                    onChange={e => {
                      const val = e.target.value;
                      UpdateValue(key, val);
                      if (key === 'name') setChildName(val);
                      if (key === 'age') setInputAge(val);
                    }}
                    dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                    value={inputValues[key] ?? ''}
                    className="border-1 text-primary border-gray-300 w-full font-semibold placeholder:font-light mt-2 placeholder:opacity-70 py-3 text-xs px-3 focus:ring-1 transition duration-200 focus:ring-black focus:border-transparent rounded-md"
                    disabled={loadDataLoading}
                  />
                  {
                    key === 'name' && 
                    <p className='mt-2 text-md font-light text-right'>{t('enterChildNameInfoText')}</p>
                  }
                </div>
              );
            })}
          </div>
        </div>
      )}

      {step === 'info' && (
        <div className='hidden lg:flex flex-col gap-4 items-center justify-center mx-auto bg-white px-4 py-6 max-w-3xl'>
          <button 
            className="w-full lg:max-w-xl text-white p-4 rounded-md bg-primary font-medium"
            id='info-step-continue' 
            onClick={() => {
              const filledInputs = Object.keys(inputs).filter(key => {
                const value = inputValues[key];
                return value !== null && value !== undefined && String(value).trim() !== '';
              });

              if (filledInputs.length < Object.keys(inputs).length) {
                toast.custom(tel => (
                  <CustomToast t={tel} type="error" message={`${t('enterAllDetailsError')}`} />
                ));
                return;
              }
              updateProductData({name: childName, age: inputAge, gender: childgender});
              handleNextStep();
            }}
            disabled={loadDataLoading}
          >
            {loadDataLoading ? `${t('infoStepDataLoadingText')}` : `${t('continue')}`}
          </button>
          <button 
            className={`mt-2 text-primary-500 text-sm font-medium ${locale === 'ar' ? 'flex' : 'flex flex-row-reverse'} items-center justify-center w-full text-center`} 
            onClick={() => setPersonalizeModal(false)}
            disabled={loadDataLoading}
          >
            {t('infoStepBackButton')} 
            <span className={`${locale === 'ar' ? 'mr-3' : 'ml-3 rotate-180'}`}>
              <AngleLeftIcon/>
            </span>
          </button>
        </div>
      )}
    </div>
  );
  
  // Helper function to get items for a specific slide
  function getCurrentSlideItemsForSlide(slideIndex: number) {
    const slideItems = [];
    
    if (slideIndex === 0) {
      // First slide: show "Add new" + up to (itemsPerSlide - 1) saved items
      slideItems.push(null); // "Add new" card always first on first slide
      
      // Add saved data items for first slide
      const firstSlideSavedItems = reversedSavedData.slice(0, itemsPerSlideWithoutAddNew);
      slideItems.push(...firstSlideSavedItems);
    } else {
      // Subsequent slides: only saved data items
      const startIndex = itemsPerSlideWithoutAddNew + (slideIndex - 1) * itemsPerSlide;
      const endIndex = Math.min(startIndex + itemsPerSlide, reversedSavedData.length);
      
      for (let i = startIndex; i < endIndex; i++) {
        if (i < reversedSavedData.length) {
          slideItems.push(reversedSavedData[i]);
        }
      }
    }
    
    return slideItems;
  }
}